import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditApi, type AuditEntry } from '@/api/audit'
import { usersApi } from '@/api/users'
import Spinner from '@/components/common/Spinner'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import clsx from 'clsx'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import HelpButton from '@/components/common/HelpButton'

const CATEGORIES = [
  { id: '',         label: 'Все' },
  { id: 'logins',   label: 'Входы' },
  { id: 'projects', label: 'Проекты' },
  { id: 'sections', label: 'Разделы' },
  { id: 'users',    label: 'Сотрудники' },
]

const ENTITY_LABELS: Record<string, string> = {
  project: 'Проект',
  section: 'Раздел',
  user:    'Пользователь',
}

const PAGE_SIZE = 50

function fmtDt(iso: string) {
  return format(new Date(iso), 'dd.MM.yyyy HH:mm', { locale: ru })
}

export default function AuditPage() {
  const [category, setCategory] = useState('')
  const [userId,   setUserId]   = useState('')
  const [page,     setPage]     = useState(0)

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
  })

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['audit-global', category, userId, page],
    queryFn: () => auditApi.getGlobal({
      category: category || undefined,
      user_id:  userId   || undefined,
      limit:    PAGE_SIZE,
      offset:   page * PAGE_SIZE,
    }).then((r) => r.data),
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Журнал действий</h1>
        <HelpButton />
      </div>

      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Категории */}
        <div className="flex items-center gap-1 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => { setCategory(c.id); setPage(0) }}
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                category === c.id
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Фильтр по пользователю */}
        <select
          value={userId}
          onChange={(e) => { setUserId(e.target.value); setPage(0) }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
        >
          <option value="">Все пользователи</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.full_name ?? u.email}</option>
          ))}
        </select>
      </div>

      {/* Таблица */}
      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-12 flex justify-center"><Spinner /></div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">Записей нет</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap">Дата / Время</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Пользователь</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Действие</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Объект</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Что изменилось</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.map((e) => (
                <AuditRow key={e.id} entry={e} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Пагинация */}
      {(entries.length === PAGE_SIZE || page > 0) && (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600">Страница {page + 1}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={entries.length < PAGE_SIZE}
            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

// ─── AuditRow ─────────────────────────────────────────────────────────────────
function AuditRow({ entry }: { entry: AuditEntry }) {
  const categoryColor: Record<string, string> = {
    logins:   'bg-blue-100 text-blue-700',
    projects: 'bg-purple-100 text-purple-700',
    sections: 'bg-amber-100 text-amber-700',
    users:    'bg-emerald-100 text-emerald-700',
    other:    'bg-gray-100 text-gray-600',
  }

  const cat = entry.category ?? 'other'
  const entityLabel = ENTITY_LABELS[entry.entity_type] ?? entry.entity_type
  const isChange = entry.action === 'update' || entry.action === 'status_change'

  return (
    <tr className="hover:bg-gray-50 transition-colors align-top">
      <td className="px-2 py-1 text-xs text-gray-500 whitespace-nowrap font-mono">
        {fmtDt(entry.created_at)}
      </td>
      <td className="px-2 py-1">
        <div className="font-medium text-gray-900">{entry.user_name ?? '-'}</div>
        <div className="text-xs text-gray-400">{entry.user_email ?? ''}</div>
      </td>
      <td className="px-2 py-1">
        <span className={clsx(
          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
          categoryColor[cat] ?? categoryColor.other,
        )}>
          {entry.action_label}
        </span>
      </td>
      <td className="px-2 py-1 text-gray-700">
        <span className="text-gray-400">{entityLabel}:</span>{' '}
        <span className="font-medium">{entry.entity_name ?? '-'}</span>
      </td>
      <td className="px-2 py-1 text-xs text-gray-600">
        {isChange && entry.changes && entry.changes.length > 0 ? (
          <div className="space-y-0 leading-tight">
            {entry.changes.map((c) => (
              <div key={c.field}>
                <span className="text-gray-500">{c.field_label}:</span>{' '}
                <span className="text-rose-600 line-through decoration-rose-300">{c.old}</span>
                {' → '}
                <span className="text-emerald-700 font-medium">{c.new}</span>
              </div>
            ))}
          </div>
        ) : entry.action === 'create' ? (
          <span className="text-gray-400">создан(а)</span>
        ) : entry.action === 'delete' ? (
          <span className="text-gray-400">удалён(а)</span>
        ) : entry.action === 'login' ? (
          <span className="text-gray-400 font-mono">{entry.ip_address ?? ''}</span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
    </tr>
  )
}
